import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useEscapeStack, useMediaQuery } from '#/shared/lib/hooks'
import { useSidePanelStore } from '#/shared/lib/side-panel'
import { cn } from '#/shared/lib/utils'
import { ChevronsLeftIcon, CloseIcon, ExpandIcon } from '#/shared/ui/icons'

import { useDetailSidebarStore } from './detail-sidebar-store'
import { DetailSurfaceProvider } from './detail-surface'
import { togglePanelWide, usePanelWide } from './use-panel-width'
import type { DetailSidebarEntry } from './detail-sidebar-store'

const CLOSE_ANIMATION_MS = 200

/**
 * Smallest usable width (viewport minus any docked phone panel) the panel needs
 * to sit beside content without smothering it — its own width (w-112.5 = 450px)
 * plus a content gutter. Below this the panel auto-closes on resize.
 */
const MIN_USABLE_WIDTH = 560

type PanelState = 'closed' | 'open' | 'closing'

function usePanelState(isOpen: boolean): PanelState {
  const [state, setState] = useState<PanelState>('closed')

  useEffect(() => {
    if (isOpen) {
      setState('open')
      return
    }
    setState((prev) => (prev === 'open' ? 'closing' : prev))
  }, [isOpen])

  useEffect(() => {
    if (state !== 'closing') return
    const timer = setTimeout(() => setState('closed'), CLOSE_ANIMATION_MS)
    return () => clearTimeout(timer)
  }, [state])

  return state
}

function useCloseOnNavigate(close: () => void) {
  const router = useRouter()
  useEffect(
    () =>
      router.subscribe('onBeforeNavigate', (event) => {
        if (event.pathChanged) close()
      }),
    [router, close],
  )
}

/**
 * Auto-close the panel when the viewport gets too narrow to show it alongside
 * content — but only in the desktop range. Below the mobile breakpoint the panel
 * is a full-screen takeover (intended), so it's left alone. A docked phone panel
 * eats horizontal room, so its width is subtracted from what's usable.
 */
function useCloseOnResize(
  isOpen: boolean,
  isDesktop: boolean,
  phonePanelOpen: boolean,
  phonePanelWidth: number,
  close: () => void,
) {
  useEffect(() => {
    if (!isOpen || !isDesktop) return
    const reserved = phonePanelOpen ? phonePanelWidth : 0
    const check = () => {
      if (window.innerWidth - reserved < MIN_USABLE_WIDTH) close()
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [isOpen, isDesktop, phonePanelOpen, phonePanelWidth, close])
}

function PanelActionButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-md text-gray-500 transition-colors select-none hover:bg-gray-100 hover:text-gray-700"
    >
      {children}
    </button>
  )
}

/** Distance from the viewport's right edge: past a docked phone panel. */
function laneRight(docked: boolean, phonePanelWidth: number): number {
  return docked ? phonePanelWidth : 0
}

function laneClass(docked: boolean, resizing: boolean, belowHeader: boolean): string {
  return cn(
    // `absolute` (not `fixed`) so the panel is anchored in the document and
    // rides the body's elastic overscroll (rubber-band) along with the rest of
    // the layout, instead of staying rigidly pinned to the viewport. The shell
    // is `h-screen overflow-hidden`, so the body never truly scrolls — visually
    // it stays put and only joins the bounce.
    // z-60 so the panel floats above the global-search overlay (z-50) rather
    // than hiding behind it — the search list stays interactive underneath.
    // `detail-sidebar-lane` lets the theme View Transition name + freeze this
    // portal so it holds still instead of being swept into the page crossfade
    // (see `html.theme-vt .detail-sidebar-lane` in index.css).
    'detail-sidebar-lane pointer-events-none absolute z-60',
    // Portaled to <body>, so the lane is measured against the viewport, not the
    // shell. `app-shell` is a plain full-height flex layout — no inset card, no
    // tab strip — so the lane runs edge to edge and only clears the global
    // header (`h-14` = 56px), which it sits flush beneath.
    'bottom-0 left-0',
    belowHeader ? 'top-14' : 'top-0',
    docked && 'overflow-hidden',
    !resizing && 'transition-[right] duration-300 ease-in-out',
  )
}

function asideClass(fullScreen: boolean, docked: boolean, closing: boolean, wide: boolean): string {
  return cn(
    'detail-sidebar-panel pointer-events-auto absolute inset-y-0 right-0 flex flex-col border-l border-gray-200 bg-white',
    // Mobile: cover the whole screen. Desktop: fixed panel width. No top border —
    // the panel opens at `top-14`, flush under the global header whose own
    // `border-b` already draws the seam; a `border-t` here just doubles it.
    // Widened: most of the viewport, capped so the list it came from stays
    // visible — the panel is still a panel, not a page.
    fullScreen ? 'w-full' : wide ? 'w-[min(72vw,900px)]' : 'w-112.5',
    // Desktop docked beside the phone panel: soft shadow off the left edge.
    // Desktop floating over content: full drop shadow. Mobile: none needed.
    !fullScreen && (docked ? 'shadow-detail-left' : 'shadow-xl'),
    closing ? 'animate-panel-out' : 'animate-panel-in',
  )
}

export function DetailSidebar() {
  const { t } = useTranslation()
  const entry = useDetailSidebarStore((state) => state.entry)
  const close = useDetailSidebarStore((state) => state.close)
  const dismiss = useDetailSidebarStore((state) => state.dismiss)
  // When the phone panel is open it occupies the right edge; shift this panel
  // left by its (resizable) width so the two sit side by side, not overlapping.
  const phonePanelOpen = useSidePanelStore((state) => state.isOpen)
  const phonePanelWidth = useSidePanelStore((state) => state.width)
  const phonePanelResizing = useSidePanelStore((state) => state.resizing)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const wide = usePanelWide()

  const lastEntryRef = useRef<DetailSidebarEntry | null>(null)
  if (entry !== null) lastEntryRef.current = entry

  const isOpen = entry !== null
  const state = usePanelState(isOpen)
  useEscapeStack(isOpen, close)
  // Route changes close the panel silently; the new URL no longer carries the
  // preview param, so there is nothing for `onClose` to clean up.
  useCloseOnNavigate(dismiss)
  // Shrinking the window past the point where the panel would smother the
  // content closes it (desktop only — mobile is a full-screen takeover).
  useCloseOnResize(isOpen, isDesktop, phonePanelOpen, phonePanelWidth, close)

  if (state === 'closed') return null
  const view = lastEntryRef.current

  const handleExpand = () => {
    // The expand target navigates away (dropping the preview param) itself, so
    // close silently to avoid a redundant URL write.
    dismiss()
    view?.onExpand?.()
  }

  // On mobile both panels cover the full screen, so don't shift left by the
  // phone-panel width — just overlay it full-width.
  const docked = isDesktop && phonePanelOpen

  return createPortal(
    // Lane wrapper: when docked beside the phone panel it clips to the area left
    // of the panel so the sidebar slides out from *under* the panel rather than
    // across it — no z-index juggling needed.
    <div
      style={{ right: laneRight(docked, phonePanelWidth) }}
      className={laneClass(docked, phonePanelResizing, isDesktop)}
    >
      <aside
        data-wide={wide && isDesktop ? 'true' : 'false'}
        className={asideClass(!isDesktop, docked, state === 'closing', wide)}
      >
        <header className="flex h-18 shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4">
          <div className="min-w-0 flex-1">{view?.header}</div>
          <div className="flex shrink-0 items-center gap-1.5">
            {isDesktop && (
              <PanelActionButton
                label={wide ? 'Narrow the panel' : 'Widen the panel'}
                onClick={togglePanelWide}
              >
                <ChevronsLeftIcon className={cn('size-4', wide && 'rotate-180')} />
              </PanelActionButton>
            )}
            {view?.onExpand && isDesktop && (
              <PanelActionButton label={t('detailSidebar.openDetail')} onClick={handleExpand}>
                <ExpandIcon className="size-4" />
              </PanelActionButton>
            )}
            <PanelActionButton label={t('detailSidebar.close')} onClick={close}>
              <CloseIcon className="size-3.5" />
            </PanelActionButton>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DetailSurfaceProvider value={true}>{view?.content}</DetailSurfaceProvider>
        </div>
      </aside>
    </div>,
    document.body,
  )
}
